import React from 'react';

import {
  MailOutlined,
  CheckCircleOutlined,
  LoginOutlined,
  SafetyOutlined,
  FormOutlined,
} from '@ant-design/icons';
import { amplifyUtils } from '@fastlessjs/backoffice-layer/dist/Body';
import { Typography, Steps } from 'antd';
import { signUp } from 'aws-amplify/auth';

import ClientIdHelpModal from '../components/ClientIdHelpModal';

export const OPEN_CLIENTID_HELP_MODAL = 'OPEN_CLIENTID_HELP_MODAL';

const { I18n, translations } = amplifyUtils;

I18n.putVocabularies(translations);

type SupportedLanguages = 'en' | 'fr';

/**
 * Transform Cognito/Lambda error messages to user-friendly messages
 * Handles UserLambdaValidationException and other pre-signup errors
 */
const errorMessageMappings: Record<SupportedLanguages, Record<string, string>> = {
  en: {
    // PreSignUp Lambda errors
    'PreSignUp failed with error': 'Registration failed',
    'Error creating client':
      'The client number provided is invalid or not found in our system. Please verify your client number and try again.',
    client:
      'The client number provided is invalid or not found in our system. Please verify your client number and try again.',
    'creating client':
      'The client number provided is invalid or not found in our system. Please verify your client number and try again.',
    'Number.*greater than Number.MAX_SAFE_INTEGER':
      'The client number provided is invalid. Please verify the number is correct.',
    'BaseError:': '',
    // Generic Cognito errors
    UserLambdaValidationException:
      'Registration validation failed. Please check your information and try again.',
  },
  fr: {
    // PreSignUp Lambda errors
    'PreSignUp failed with error': "L'inscription a échoué",
    'Error creating client':
      'Le numéro de client fourni est invalide ou introuvable dans notre système. Veuillez vérifier votre numéro de client et réessayer.',
    client:
      'Le numéro de client fourni est invalide ou introuvable dans notre système. Veuillez vérifier votre numéro de client et réessayer.',
    'creating client':
      'Le numéro de client fourni est invalide ou introuvable dans notre système. Veuillez vérifier votre numéro de client et réessayer.',
    'Number.*greater than Number.MAX_SAFE_INTEGER':
      'Le numéro de client fourni est invalide. Veuillez vérifier que le numéro est correct.',
    'BaseError:': '',
    // Generic Cognito errors
    UserLambdaValidationException:
      "La validation de l'inscription a échoué. Veuillez vérifier vos informations et réessayer.",
  },
};

/**
 * Sanitize and transform error messages to be user-friendly
 * @param message - Raw error message from Cognito/Lambda
 * @param language - Current language (en or fr)
 * @returns User-friendly error message
 */
const transformErrorMessage = (message: string, language: SupportedLanguages): string => {
  if (!message)
    return language === 'fr' ? 'Une erreur est survenue.' : 'An error occurred.';

  const mappings = errorMessageMappings[language] || errorMessageMappings.en;

  // Check for client-related errors first (most specific)
  if (
    message.includes('client') ||
    message.includes('creating client') ||
    message.includes('Error creating client')
  ) {
    return mappings['Error creating client'];
  }

  // Check for Number.MAX_SAFE_INTEGER error (technical error from DynamoDB)
  if (message.includes('greater than Number.MAX_SAFE_INTEGER')) {
    return mappings['Number.*greater than Number.MAX_SAFE_INTEGER'];
  }

  // Check for PreSignUp Lambda errors
  if (message.includes('PreSignUp failed with error')) {
    if (message.includes('BaseError:')) {
      const baseErrorMatch = message.match(/BaseError:\s*(.+)/);
      if (baseErrorMatch && baseErrorMatch[1]) {
        const baseError = baseErrorMatch[1].trim();

        if (baseError.includes('client') || baseError.includes('creating')) {
          return mappings['Error creating client'];
        }

        return language === 'fr'
          ? `Erreur lors de l'inscription : ${baseError}`
          : `Registration error: ${baseError}`;
      }
    }

    return message;
  }

  // Check for UserLambdaValidationException (more generic)
  if (message.includes('UserLambdaValidationException')) {
    return mappings.UserLambdaValidationException;
  }

  return message;
};

I18n.putVocabulariesForLanguage('en', {
  'Enter your Username': 'Email',
  'Enter your Password': 'Password',
  'Please confirm your Password': 'Confirm your password',
  'Password did not conform with policy: Password must have symbol characters':
    'Password must contain at least one special character (!@#$%^&* etc.)',
  'Invalid phone number format. Please use a valid French mobile number (e.g., 06 12 34 56 78 or +33 6 12 34 56 78)':
    'Invalid phone number format. Please use a valid French mobile number (e.g., 06 12 34 56 78 or +33 6 12 34 56 78)',
});

I18n.putVocabulariesForLanguage('fr', {
  'Enter your Username': 'Email',
  'Enter your Password': 'Mot de passe',
  'Please confirm your Password': 'Confirmer votre mot de passe',
  'Password did not conform with policy: Password must have symbol characters':
    'Le mot de passe doit contenir au moins un caractère spécial (!@#$%^&* etc.)',
  'Invalid phone number format. Please use a valid French mobile number (e.g., 06 12 34 56 78 or +33 6 12 34 56 78)':
    'Format de numéro de téléphone invalide. Veuillez utiliser un numéro de mobile français valide (ex. : 06 12 34 56 78 ou +33 6 12 34 56 78)',
});

// Export the transform function for use in services
export { transformErrorMessage };

interface OrganizationTheme {
  template?: { colors?: { primary?: string; secondary?: string } };
  logos?: { dark?: string; light?: string; auth?: string };
  favicon?: string;
}

const setLanguageBasedOnBrowser = (): SupportedLanguages => {
  const browserLanguage =
    (navigator.language.substring(0, 2) as SupportedLanguages) || 'en';
  const supportedLanguages: SupportedLanguages[] = ['en', 'fr'];
  const defaultLanguage: SupportedLanguages = 'en';

  const languageToSet = supportedLanguages.includes(browserLanguage)
    ? browserLanguage
    : defaultLanguage;

  I18n.setLanguage(languageToSet);
  return languageToSet;
};

const getTranslations = (language: SupportedLanguages) => {
  const translation = {
    en: {
      clientIdLabel: 'Client Number',
      clientIdPlaceholder: 'Client Number - e.g., 01234567',
      givenNameLabel: 'First Name',
      givenNamePlaceholder: 'First Name',
      familyNameLabel: 'Last Name',
      familyNamePlaceholder: 'Last Name',
      phoneNumberLabel: 'Phone Number',
      phoneNumberPlaceholder: 'Phone Number - e.g., 6 12 34 56 78',
      emailLabel: 'Email',
      emailPlaceholder: 'Email address',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Password',
      passwordDescriptiveText:
        'Password must be at least 8 characters long, including an uppercase letter, a lowercase letter, a number, and a special character',
      // Enhanced confirmation page translations
      confirmSignUpTitle: '📧 Check your inbox!',
      confirmSignUpSubtitle: 'We sent you a verification email',
      confirmSignUpContent:
        'A confirmation email has been sent to your email address. Please check your inbox and click the verification link to activate your account.',
      confirmSignUpSpamNote:
        "💡 Tip: If you don't see the email, please check your spam or junk folder.",
      confirmSignUpExpiryNote: '⏰ The verification link expires in 24 hours.',
      confirmSignUpLink: '← Back to login',
      // Steps
      stepFillForm: 'Fill the form',
      stepFillFormDesc: 'Enter your information',
      stepVerifyEmail: 'Verify email',
      stepVerifyEmailDesc: 'Click the link in the email',
      stepAccessAccount: 'Access account',
      stepAccessAccountDesc: 'Start using your account',
      // What happens next
      whatHappensNext: 'What happens next?',
      step1Title: 'Open the email',
      step1Desc: 'Look for an email from Bragard in your inbox',
      step2Title: 'Click the verification link',
      step2Desc: 'The link will confirm your email address',
      step3Title: "You're all set!",
      step3Desc: 'Your account will be activated automatically',
    },
    fr: {
      clientIdLabel: 'Numéro de client',
      clientIdPlaceholder: 'Numéro de client - Ex.: 01234567',
      givenNameLabel: 'Prénom',
      givenNamePlaceholder: 'Prénom',
      familyNameLabel: 'Nom',
      familyNamePlaceholder: 'Nom',
      phoneNumberLabel: 'Numéro de téléphone',
      phoneNumberPlaceholder: 'Numéro de téléphone - Ex.: 6 12 34 56 78',
      emailLabel: 'Email',
      emailPlaceholder: 'Adresse email',
      passwordLabel: 'Mot de passe',
      passwordPlaceholder: 'Mot de passe',
      passwordDescriptiveText:
        'Le mot de passe doit contenir au moins 8 caractères, incluant une majuscule, une minuscule, un chiffre et un caractère spécial',
      // Enhanced confirmation page translations
      confirmSignUpTitle: '📧 Consultez votre boîte mail !',
      confirmSignUpSubtitle: 'Nous vous avons envoyé un email de vérification',
      confirmSignUpContent:
        'Un email de confirmation a été envoyé à votre adresse email. Veuillez consulter votre boîte de réception et cliquer sur le lien de vérification pour activer votre compte.',
      confirmSignUpSpamNote:
        "💡 Conseil : Si vous ne voyez pas l'email, vérifiez votre dossier spam ou courrier indésirable.",
      confirmSignUpExpiryNote: '⏰ Le lien de vérification expire dans 24 heures.',
      confirmSignUpLink: '← Retour à la connexion',
      // Steps
      stepFillForm: 'Remplir le formulaire',
      stepFillFormDesc: 'Entrez vos informations',
      stepVerifyEmail: 'Vérifier email',
      stepVerifyEmailDesc: "Cliquez sur le lien dans l'email",
      stepAccessAccount: 'Accéder au compte',
      stepAccessAccountDesc: 'Commencez à utiliser votre compte',
      // What happens next
      whatHappensNext: 'Que se passe-t-il ensuite ?',
      step1Title: "Ouvrez l'email",
      step1Desc: 'Recherchez un email de Bragard dans votre boîte de réception',
      step2Title: 'Cliquez sur le lien de vérification',
      step2Desc: 'Le lien confirmera votre adresse email',
      step3Title: 'Vous êtes prêt !',
      step3Desc: 'Votre compte sera activé automatiquement',
    },
  };

  return translation[language] || translation.en;
};

// Default auth logo path
const DEFAULT_AUTH_LOGO = '/logo_auth.svg';

/**
 * Create sign-up configuration with optional organization theme
 * @param theme - Organization theme from theme.json (optional)
 */
export const createSignUpConfig = (theme?: OrganizationTheme | null) => {
  const language = setLanguageBasedOnBrowser();
  const tr = getTranslations(language);

  // Passer la langue détectée au composant ClientIdHelpModal
  const currentLanguage = language;

  const authLogo = theme?.logos?.auth || DEFAULT_AUTH_LOGO;

  return {
    services: {
      async handleSignUp(formData: Record<string, any>) {
        try {
          const { username, password, attributes, options: signUpOptions } = formData;
          // Amplify v6 / Authenticator passe les attributs dans options.userAttributes (SignUpInput).
          // Ancienne forme (v5) : attributs à la racine sous `attributes`.
          const userAttributes = signUpOptions?.userAttributes ?? attributes;
          let attrsSource: 'options.userAttributes' | 'legacy.attributes' | 'none' =
            'none';
          if (signUpOptions?.userAttributes) {
            attrsSource = 'options.userAttributes';
          } else if (attributes) {
            attrsSource = 'legacy.attributes';
          }

          console.log('[SignUp] submit', {
            username,
            hasPassword: Boolean(password),
            attrsSource,
            userAttributeKeys: userAttributes ? Object.keys(userAttributes) : [],
            'custom:clientId': userAttributes?.['custom:clientId'],
          });

          const result = await signUp({
            username,
            password,
            options: {
              ...signUpOptions,
              userAttributes,
              autoSignIn: true,
            },
          });

          console.log('[SignUp] success', {
            isSignUpComplete: result?.isSignUpComplete,
            nextStep: result?.nextStep?.signUpStep,
          });

          return result;
        } catch (error: any) {
          const originalMessage = error?.message || error?.toString() || '';
          console.warn('[SignUp] error', {
            name: error?.name,
            message: originalMessage,
          });

          const friendlyMessage = transformErrorMessage(originalMessage, language);

          const transformedError = new Error(friendlyMessage);
          transformedError.name = error?.name || 'SignUpError';

          throw transformedError;
        }
      },
    },

    formFields: {
      signIn: {
        username: {
          label: tr.emailLabel,
          placeholder: tr.emailPlaceholder,
          isRequired: true,
          type: 'email',
        },
        password: {
          label: tr.passwordLabel,
          placeholder: tr.passwordPlaceholder,
          isRequired: true,
          type: 'password',
        },
      },

      signUp: {
        'custom:clientId': {
          order: 1,

          label: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span>{tr.clientIdLabel}</span>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event(OPEN_CLIENTID_HELP_MODAL))}
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  border: '1px solid #d9d9d9',
                  background: '#fff',
                  cursor: 'pointer',
                  fontWeight: 700,
                  lineHeight: '18px',
                  padding: 0,
                }}
                aria-label="Aide numéro client"
              >
                ?
              </button>
            </span>
          ) as any,
          placeholder: tr.clientIdPlaceholder,
          type: 'text',
          isRequired: true,
        },

        given_name: {
          order: 2,
          label: tr.givenNameLabel,
          placeholder: tr.givenNamePlaceholder,
          type: 'text',
          isRequired: true,
        },
        family_name: {
          order: 3,
          label: tr.familyNameLabel,
          placeholder: tr.familyNamePlaceholder,
          type: 'text',
          isRequired: true,
        },
        phone_number: {
          order: 5,
          label: tr.phoneNumberLabel,
          placeholder: tr.phoneNumberPlaceholder,
          type: 'tel',
          isRequired: true,
          dialCode: '+33',
        },
        username: {
          order: 5,
          label: tr.emailLabel,
          placeholder: tr.emailPlaceholder,
          type: 'email',
          isRequired: true,
        },
        password: {
          order: 6,
          label: tr.passwordLabel,
          placeholder: tr.passwordPlaceholder,
          isRequired: true,
          descriptiveText: (
            <Typography.Text
              type="secondary"
              style={{ fontSize: '12px', lineHeight: '0' }}
            >
              {tr.passwordDescriptiveText}
            </Typography.Text>
          ),
        },
      },
    },

    components: {
      Header() {
        const [helpOpen, setHelpOpen] = React.useState(false);

        React.useEffect(() => {
          const handler = () => setHelpOpen(true);
          window.addEventListener(OPEN_CLIENTID_HELP_MODAL, handler);
          return () => window.removeEventListener(OPEN_CLIENTID_HELP_MODAL, handler);
        }, []);

        return (
          <div style={{ textAlign: 'center' }}>
            <img
              src={authLogo}
              alt="Logo"
              style={{
                width: '300px',
                height: '80px',
                backgroundColor: 'white',
                border: '1px solid rgba(239, 239, 239, 0.91)',
                boxShadow:
                  '0 0 6px 0 rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                borderRadius: '16px 16px 0 0',
              }}
            />

            <ClientIdHelpModal
              open={helpOpen}
              onClose={() => setHelpOpen(false)}
              language={currentLanguage}
            />
          </div>
        );
      },

      ConfirmSignUp: {
        Header() {
          return (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                maxWidth: '440px',
                margin: '0 auto',
              }}
            >
              <Steps
                size="small"
                current={1}
                direction="vertical"
                style={{ marginBottom: '32px' }}
                items={[
                  {
                    title: tr.stepFillForm,
                    subTitle: tr.stepFillFormDesc,
                    icon: <FormOutlined />,
                    status: 'finish',
                  },
                  {
                    title: tr.stepVerifyEmail,
                    subTitle: tr.stepVerifyEmailDesc,
                    icon: <MailOutlined />,
                    status: 'process',
                  },
                  {
                    title: tr.stepAccessAccount,
                    subTitle: tr.stepAccessAccountDesc,
                    icon: <LoginOutlined />,
                    status: 'wait',
                  },
                ]}
              />

              <div
                style={{
                  background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                  borderRadius: '16px',
                  padding: '32px 24px',
                  marginBottom: '24px',
                }}
              >
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px auto',
                    boxShadow: '0 8px 24px rgba(59, 130, 246, 0.3)',
                  }}
                >
                  <MailOutlined style={{ fontSize: '36px', color: '#ffffff' }} />
                </div>

                <h1
                  style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: '#1a1a2e',
                    margin: '0 0 8px 0',
                  }}
                >
                  {tr.confirmSignUpTitle}
                </h1>
                <p
                  style={{
                    fontSize: '16px',
                    color: '#64748b',
                    margin: '0 0 16px 0',
                  }}
                >
                  {tr.confirmSignUpSubtitle}
                </p>
                <p
                  style={{
                    fontSize: '14px',
                    color: '#475569',
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {tr.confirmSignUpContent}
                </p>
              </div>

              <div
                style={{
                  textAlign: 'left',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                }}
              >
                <h3
                  style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1a1a2e',
                    margin: '0 0 16px 0',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {tr.whatHappensNext}
                </h3>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      background: '#f0f9ff',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      flexShrink: 0,
                    }}
                  >
                    <MailOutlined style={{ color: '#3b82f6', fontSize: '16px' }} />
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1a1a2e',
                        margin: '0 0 2px 0',
                      }}
                    >
                      {tr.step1Title}
                    </p>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                      {tr.step1Desc}
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '16px',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      background: '#f0f9ff',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      flexShrink: 0,
                    }}
                  >
                    <SafetyOutlined style={{ color: '#3b82f6', fontSize: '16px' }} />
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1a1a2e',
                        margin: '0 0 2px 0',
                      }}
                    >
                      {tr.step2Title}
                    </p>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                      {tr.step2Desc}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      background: '#f0fdf4',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '12px',
                      flexShrink: 0,
                    }}
                  >
                    <CheckCircleOutlined style={{ color: '#22c55e', fontSize: '16px' }} />
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#1a1a2e',
                        margin: '0 0 2px 0',
                      }}
                    >
                      {tr.step3Title}
                    </p>
                    <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                      {tr.step3Desc}
                    </p>
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: '#fffbeb',
                  borderLeft: '4px solid #f59e0b',
                  padding: '12px 16px',
                  borderRadius: '0 8px 8px 0',
                  marginBottom: '16px',
                  textAlign: 'left',
                }}
              >
                <p style={{ fontSize: '13px', color: '#b45309', margin: 0 }}>
                  {tr.confirmSignUpSpamNote}
                </p>
              </div>

              <div
                style={{
                  background: '#f0f9ff',
                  borderLeft: '4px solid #3b82f6',
                  padding: '12px 16px',
                  borderRadius: '0 8px 8px 0',
                  marginBottom: '24px',
                  textAlign: 'left',
                }}
              >
                <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
                  {tr.confirmSignUpExpiryNote}
                </p>
              </div>

              <a
                href="/"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: '#3b82f6',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  gap: '4px',
                }}
              >
                {tr.confirmSignUpLink}
              </a>
            </div>
          );
        },
      },
    },
  };
};

export default createSignUpConfig();
